import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Katalog } from '../shared/model/katalog.model';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';

/** Response type katalog **/
type EntityResponseType = HttpResponse<Katalog>;
/** Response type katalog array **/
type EntityArrayResponseType = HttpResponse<Katalog[]>;

@Injectable({ providedIn: 'root' })
export class KatalogeService {
  /** Api url **/
  public resourceUrl = environment.apiConfig.uri + 'api/kataloge';

  /** Creates the http client **/
  constructor(protected http: HttpClient) {}

  /** API call to update the katalog item **/
  update(katalog: Katalog): Observable<EntityResponseType> {
    return this.http.put<Katalog>(
      `${this.resourceUrl}/${katalog.id}`,
      katalog,
      { observe: 'response' },
    );
  }

  /** API call to get all kataloge **/
  query(): Observable<EntityArrayResponseType> {
    return this.http.get<Katalog[]>(this.resourceUrl, { observe: 'response' });
  }

  /** API call to get katalog item by ID **/
  getByID(id: string): Observable<Katalog> {
    return this.http.get<Katalog>(`${this.resourceUrl}/${id}`, {});
  }

  /**
   * NP20-889: Laedt gezielt die angefragten Master-Kataloge. Bewusst einzeln statt ueber die
   * Gesamtliste — der Berater darf im eigenen Profil nur `/{id}` lesen, nicht den kompletten
   * Katalogbestand. Bei ein bis drei Katalogen pro Berater faellt das nicht ins Gewicht.
   *
   * Jeder Aufruf faengt seinen Fehler selbst ab (Review PR #702). Vorher hing an einem
   * gemeinsamen `forkJoin` alles am schwaechsten Glied: ein einzelner fehlschlagender GET —
   * etwa ein geloeschter Katalog, dessen Zuweisung noch nicht kaskadiert weg ist — liess die
   * ganze Aufloesung leer laufen, und weil `resolveAssignments` jede Zuweisung ohne Master
   * verwirft, verschwanden *alle* BA-Kataloge statt nur des defekten.
   *
   * Nicht ladbare Kataloge fehlen im Ergebnis. Sie werden beim naechsten Aufruf erneut
   * versucht, weil der Aufrufer nur Erfolge in seinen Cache legt.
   */
  getByIds(ids: string[]): Observable<Katalog[]> {
    if (ids.length === 0) {
      return of([]);
    }
    return forkJoin(
      ids.map((id) => this.getByID(id).pipe(catchError(() => of(null)))),
    ).pipe(map((kataloge) => kataloge.filter((katalog): katalog is Katalog => katalog !== null)));
  }

  /** API call to create a new katalog item **/
  create(
    kuerzel: string,
    rvseVersion: number,
    preisgruppe: number,
    bezeichnung: string,
  ) {
    const bez = { value: bezeichnung };
    return this.http.post<Katalog>(
      `${this.resourceUrl}/${kuerzel}/${rvseVersion}/${preisgruppe}`,
      bez,
      { observe: 'response' },
    );
  }
}
